"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, Phone, ArrowRight, MapPin, CircleCheck as CheckCircle2, Search, Sparkles, Mail } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const categories = [{
  id: "all",
  label: "All Questions"
}, {
  id: "gbp",
  label: "Google Business Profile"
}, {
  id: "strategy",
  label: "Strategy & Timing"
}, {
  id: "freelance",
  label: "Direct Consultant Access"
}];
const faqItems = [{
  id: "faq-1",
  category: "strategy",
  question: "How long does it take to see local SEO results for my Gloucester business?",
  answer: "Most local Gloucester businesses start seeing improved map pack rankings and increased customer inquiries within 3 to 6 months. Initial quick wins often happen faster when I optimize your Google Business Profile and resolve technical local errors. Local search is high-intent, meaning early ranking improvements convert directly into phone calls and direction requests."
}, {
  id: "faq-2",
  category: "freelance",
  question: "Why should I hire you as an independent SEO consultant rather than a Gloucester agency?",
  answer: "When you hire me, you work directly with Yassine Essaadi—the expert crafting and implementing your strategy. Agency clients are often passed down to junior account managers. Working with me guarantees direct communication, personalized attention, senior-level technical expertise, and tailored local strategies with zero middleman markup."
}, {
  id: "faq-3",
  category: "gbp",
  question: "How do you optimize my Google Business Profile for Gloucester searchers?",
  answer: "I conduct a thorough audit of your Google Business Profile, selecting hyper-relevant primary and secondary categories, creating search-optimized service listings, setting up targeted geotagged media, and structuring a systematic review management strategy. I also ensure complete NAP (Name, Address, Phone) consistency across all UK local directories."
}, {
  id: "faq-4",
  category: "strategy",
  question: "Do I need a physical address in Gloucester to rank locally on Google Maps?",
  answer: "You do not necessarily need a public retail location, but you do need a verified location or a service area address within Gloucestershire. If you serve customers at their locations (such as tradespeople or mobile consultants), I configure your profile as a Service Area Business (SAB) to maximize reach across Gloucester, Cheltenham, and surrounding areas."
}, {
  id: "faq-5",
  category: "strategy",
  question: "How do local citations and Gloucestershire directory listings impact my rankings?",
  answer: "Local citations act as online trust signals that confirm your business location to Google. I build high-quality, manual citations across reputable UK and Gloucestershire business directories (such as Yell, Thomson Local, and local chamber networks) to strengthen your local geographic authority and boost your Map Pack rankings."
}, {
  id: "faq-6",
  category: "strategy",
  question: "What is the difference between local SEO and national SEO for a UK business?",
  answer: "Local SEO focuses on capturing geographically targeted queries (e.g., 'solicitors in Gloucester' or 'plumber near me') and optimizes heavily for Google Maps and local pack visibility. National SEO focuses on ranking across broad UK-wide keyword terms where location is non-specific. My local SEO strategy targets buyers who are actively ready to purchase in your specific service region."
}, {
  id: "faq-7",
  category: "freelance",
  question: "Can local SEO help my Gloucester business outrank larger national competitors?",
  answer: "Yes, absolutely. Local SEO is the ultimate equalizer for regional businesses. Google prioritizes proximity, local relevance, and localized authority for geo-targeted searches. By optimizing your local signals and earning local backlinks, I help your business displace national brand listings in the local 3-pack."
}, {
  id: "faq-8",
  category: "strategy",
  question: "How do you handle local keyword research for Gloucester and surrounding areas like Cheltenham?",
  answer: "I analyze search volume and user intent for Gloucester alongside neighboring towns such as Cheltenham, Stroud, Tewkesbury, and Cirencester. I uncover high-converting localized phrases, geo-targeted long-tail queries, and neighborhood-specific searches that your competitors frequently overlook."
}, {
  id: "faq-9",
  category: "freelance",
  question: "What deliverables will I receive in our monthly local SEO consultation?",
  answer: "Every month, I deliver transparent performance reporting detailing rankings, organic website traffic, and Google Map interactions (calls, website visits, direction requests). You receive clear, human summaries of executed tasks, local citation updates, and a strategic roadmap for the upcoming month."
}, {
  id: "faq-10",
  category: "gbp",
  question: "How do customer reviews affect my local search visibility in Gloucester?",
  answer: "Customer reviews are one of the strongest local ranking and conversion factors. Google evaluates review quantity, velocity, star rating, and keywords used in review responses. I guide you through automated or structured review request workflows that steadily build trust with both search algorithms and local prospective clients."
}, {
  id: "faq-11",
  category: "strategy",
  question: "What happens if Google updates its algorithm—will my Gloucester business lose traffic?",
  answer: "I follow ethical, white-hat local SEO practices focused on sustainable technical performance, real authority, and search engine guidelines. Unlike black-hat spam tactics that risk penalty during core updates, my quality-first approach ensures your local search presence remains stable and resilient."
}, {
  id: "faq-12",
  category: "freelance",
  question: "How do we get started with a tailored local SEO strategy for my business?",
  answer: "It starts with a direct conversation. You can book a free, no-obligation local SEO consultation with me. I will audit your current Gloucester search footprint, identify immediate growth opportunities, and walk you through a tailored action plan designed specifically for your goals."
}];
export default function Faq() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const shouldReduceMotion = useReducedMotion();
  const filteredFaqs = faqItems.filter(item => {
    const matchesCategory = activeCategory === "all" || item.category === activeCategory;
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <section id="localseofaq" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 px-4 sm:px-6 lg:px-8 border-b border-border overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 size-96 rounded-full bg-primary/5 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 left-0 size-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="mx-auto max-w-6xl relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 py-1 px-3 border-primary/30 text-primary bg-primary/5">
            <MapPin className="size-3.5 mr-1 text-primary" />
            Gloucester Local SEO Insights
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Frequently Asked Questions About <span className="text-primary font-bold">Local SEO</span>
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Clear, honest answers directly from Yassine Essaadi. No agency fluff or jargon—just straightforward clarity on how I help Gloucester businesses attract more local clients.
          </p>

          {}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-primary" />
              <span>100% Direct Consultant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Tailored Gloucester Search Strategy</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Transparent Reporting</span>
            </div>
          </div>
        </div>

        {}
        <div className="max-w-4xl mx-auto mb-10 space-y-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
            <input type="text" placeholder="Search local SEO questions (e.g. Google Maps, agency vs freelance, timing)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-card text-card-foreground border border-border rounded-xl shadow-xs text-sm sm:text-base focus:outline-hidden focus:ring-2 focus:ring-primary/50 transition-all" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {categories.map((cat, i) => <Button key={cat.id} data-index={i} variant={activeCategory === cat.id ? "default" : "outline"} size="sm" onClick={() => setActiveCategory(cat.id)} className={`rounded-full transition-all text-xs sm:text-sm font-medium ${activeCategory === cat.id ? "bg-primary text-primary-foreground shadow-xs" : "bg-background text-foreground hover:bg-muted"}`}>
                {cat.label}
              </Button>)}
          </div>
        </div>

        {}
        <div className="max-w-4xl mx-auto bg-card rounded-2xl border border-border p-4 sm:p-8 shadow-sm">
          {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="w-full space-y-3">
              {filteredFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border border-border/80 rounded-xl px-4 sm:px-6 bg-background/60 transition-colors duration-200 hover:border-primary/40 overflow-hidden">
                  <AccordionTrigger className="text-left font-default font-semibold text-primary hover:text-primary/90 text-base sm:text-lg py-4 sm:py-5 hover:no-underline group">
                    <span className="flex items-start gap-3">
                      <HelpCircle className="size-5 text-primary shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8 sm:pl-8 pb-5 pt-1">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion> : <div className="text-center py-12 px-4">
              <HelpCircle className="size-12 text-muted-foreground mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium text-foreground">No matching questions found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try adjusting your search terms or contact me directly to ask your specific local SEO question.
              </p>
              <Button variant="outline" size="sm" onClick={() => {
            setSearchQuery("");
            setActiveCategory("all");
          }} className="mt-4">
                Reset Filters
              </Button>
            </div>}
        </div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-12 sm:mt-16 max-w-4xl mx-auto rounded-2xl bg-secondary/50 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
              <Sparkles className="size-4 text-accent" />
              <span>Direct Expert Communication</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default">
              Have a specific question about your Gloucester business?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl">
              You work directly with me—Yassine Essaadi. Book a free 30-minute local SEO consultation to discuss your rankings, competitors, and growth goals.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
            <Link to="/ContactPage" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto font-medium shadow-xs group">
                <MessageSquare className="size-4 mr-1.5" />
                Book Free Consultation
                <ArrowRight className="size-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="mailto:contact@ye-consultancy.com" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                <Mail className="size-4 mr-1.5" />
                Email Yassine
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}